import { formatDate } from '@angular/common';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { unparse, UnparseConfig, UnparseObject } from 'papaparse';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private readonly document = inject(DOCUMENT);

  generateCsvFileName(prefix: string): string {
    const now = new Date();
    const formattedDate = formatDate(now, 'yyyyMMdd_HHmmss', 'en-US');
    return `${prefix}_${formattedDate}.csv`;
  }

  parseToCsv<T extends Record<string, unknown>>(
    data: T[] | { fields?: string[]; data: T[] },
    config?: UnparseConfig,
  ): string {
    try {
      let csvData: UnparseObject<T>;

      // Helper function to extract all unique fields from an array of objects
      const extractAllFields = (items: T[]): string[] => {
        const allFields = items.reduce((fields, obj) => {
          Object.keys(obj).forEach((key) => fields.add(key));
          return fields;
        }, new Set<string>());
        return Array.from(allFields);
      };

      if (Array.isArray(data)) {
        const fields = extractAllFields(data);
        csvData = { fields, data };
      } else {
        // Use provided fields if available, otherwise extract all unique fields
        const fields = data.fields ?? extractAllFields(data.data);
        csvData = { fields, data: data.data };
      }

      return unparse(csvData, config);
    } catch (error) {
      console.error('Error parsing data to CSV:', error);
      throw new Error('Failed to generate CSV content');
    }
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = this.document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;

    this.document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    this.document.body.removeChild(a);
  }

  downloadCsv<T extends Record<string, unknown>>(
    data: T[] | { fields?: string[]; data: T[] },
    filename: string,
    config?: Omit<UnparseConfig, 'escapeFormulae'>,
  ): void {
    const csvContent = this.parseToCsv(data, {
      ...config,
      escapeFormulae: true,
    });
    // UTF-8 with BOM https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
    const blob = new Blob(['\ufeff' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    this.downloadBlob(blob, filename);
  }

  downloadFile(href: string, filename: string): void {
    const a = this.document.createElement('a');
    a.style.display = 'none';
    a.href = href;
    a.download = filename;

    this.document.body.appendChild(a);
    a.click();

    this.document.body.removeChild(a);
  }
}
